package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMBottomSheetHeightVar
import ai.wagemix.web.theme.WMCardTitleTextStyle
import ai.wagemix.web.theme.WMDialogGapVar
import ai.wagemix.web.theme.WMDialogPaddingVar
import ai.wagemix.web.theme.WMDialogWidthVar
import ai.wagemix.web.theme.WMDialogWidthWideVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMPanelCornerVar
import ai.wagemix.web.theme.WMPageGutterCompactVar
import ai.wagemix.web.theme.WMSheetHeightCompactVar
import ai.wagemix.web.theme.WMSheetHeightVar
import ai.wagemix.web.theme.WMSheetWidthExpandedVar
import ai.wagemix.web.theme.WMSheetWidthLargeVar
import ai.wagemix.web.theme.wagemix
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.FSMotionMediumVar
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.translateX
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.AnimationTimingFunction
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import com.varabyte.kobweb.compose.css.Overflow
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

sealed interface WMDialogKind: ComponentKind

/** Penpot `Wagemix / Dialog`: a native modal `<dialog>` with the panel corner and raised shadow. */
val WMDialogStyle = CssStyle<WMDialogKind> {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .backgroundColor(colors.surface)
            .color(colors.onSurface)
            .border(0.px)
            .borderRadius(WMPanelCornerVar.value())
            .padding(WMDialogPaddingVar.value())
            .maxWidth(WMDialogWidthVar.value())
            .maxHeight(90.vh)
            .overflow(Overflow.Auto)
            .boxShadow(offsetY = 12.px, blurRadius = 32.px, color = colorMode.toPalette().wagemix.shadow)
            .styleModifier { property("width", "calc(100% - 2 * ${WMDialogPaddingVar.value()})") }
    }
    cssRule("::backdrop") {
        Modifier.backgroundColor(colorMode.toPalette().fs.scrim)
    }
}

/** The wider dialog (Share mix). */
val WMWideDialogVariant = WMDialogStyle.addVariant {
    base { Modifier.maxWidth(WMDialogWidthWideVar.value()) }
}

/**
 * A sheet (the occupation picker), sized only by the viewport so it is never cut off and never
 * resizes with its content. Width: the full width less the compact page gutter on each side up to
 * Expanded, then 80% of the viewport, and 60% from Large up, never wider than the viewport less
 * the gutters. Height: [WMSheetHeightCompactVar] below Medium, [WMSheetHeightVar] from Medium up,
 * never taller than the viewport less the gutters. The content area takes the height left under
 * the title and above the actions when its root uses [WMDialogFillStyle]; what does not fit
 * scrolls inside it.
 */
val WMSheetDialogVariant = WMDialogStyle.addVariant {
    val fullWidth = "calc(100% - 2 * ${WMPageGutterCompactVar.value()})"
    val fullHeight = "calc(100dvh - 2 * ${WMPageGutterCompactVar.value()})"
    base {
        Modifier.styleModifier {
            property("max-width", "none")
            property("width", fullWidth)
            property("max-height", "none")
            property("height", "min(${WMSheetHeightCompactVar.value()}, $fullHeight)")
            property("overflow", "hidden")
        }
    }
    FSBreakpoint.Medium { Modifier.styleModifier { property("height", "min(${WMSheetHeightVar.value()}, $fullHeight)") } }
    FSBreakpoint.Expanded { Modifier.styleModifier { property("width", "min(${WMSheetWidthExpandedVar.value()}, $fullWidth)") } }
    FSBreakpoint.Large { Modifier.styleModifier { property("width", "min(${WMSheetWidthLargeVar.value()}, $fullWidth)") } }
}

/** The side panel arriving from the right edge. */
val WMSidePanelInKeyframes = Keyframes {
    from { Modifier.translateX(100.percent) }
    to { Modifier.translateX(0.percent) }
}

/** The bottom sheet rising from the bottom edge. */
val WMBottomSheetInKeyframes = Keyframes {
    from { Modifier.translateY(100.percent) }
    to { Modifier.translateY(0.percent) }
}

/**
 * The occupation picker (design deviation G1, Penpot `Mix Builder / Large · Browse Occupations` and `Compact · Browse
 * Occupations`): a bottom sheet below Expanded, full width and [WMBottomSheetHeightVar] tall with rounded top corners;
 * from Expanded up a full-height panel against the right edge, [WMSheetWidthExpandedVar] then [WMSheetWidthLargeVar]
 * wide, over the scrim. Both slide in, unless the reader prefers reduced motion. The content area takes the height
 * left under the title and above the actions when its root uses [WMDialogFillStyle].
 */
val WMSidePanelDialogVariant = WMDialogStyle.addVariant {
    base {
        Modifier
            .animation(WMBottomSheetInKeyframes.toAnimation(duration = FSMotionMediumVar.value(), timingFunction = AnimationTimingFunction.EaseOut))
            .styleModifier {
                property("margin", "auto 0 0 0")
                property("max-width", "none")
                property("width", "100%")
                property("max-height", "none")
                property("height", WMBottomSheetHeightVar.value().toString())
                property("overflow", "hidden")
                property("border-radius", "${WMPanelCornerVar.value()} ${WMPanelCornerVar.value()} 0 0")
            }
    }
    FSBreakpoint.Expanded {
        Modifier
            .animation(WMSidePanelInKeyframes.toAnimation(duration = FSMotionMediumVar.value(), timingFunction = AnimationTimingFunction.EaseOut))
            .styleModifier {
                property("margin", "0 0 0 auto")
                property("width", WMSheetWidthExpandedVar.value().toString())
                property("height", "100dvh")
                property("border-radius", "0")
            }
    }
    FSBreakpoint.Large { Modifier.styleModifier { property("width", WMSheetWidthLargeVar.value().toString()) } }
    cssRule(WMReducedMotion) { Modifier.styleModifier { property("animation", "none") } }
}

/**
 * A short bottom sheet at every size, as tall as its content up to [WMBottomSheetHeightVar] (the phone filters, design
 * deviation H1): full width against the bottom edge with rounded top corners, sliding up unless the reader prefers
 * reduced motion. It is opened only below Medium; nothing sizes it for wider windows.
 */
val WMBottomSheetDialogVariant = WMDialogStyle.addVariant {
    base {
        Modifier
            .animation(WMBottomSheetInKeyframes.toAnimation(duration = FSMotionMediumVar.value(), timingFunction = AnimationTimingFunction.EaseOut))
            .styleModifier {
                property("margin", "auto 0 0 0")
                property("max-width", "none")
                property("width", "100%")
                property("max-height", WMBottomSheetHeightVar.value().toString())
                property("border-radius", "${WMPanelCornerVar.value()} ${WMPanelCornerVar.value()} 0 0")
            }
    }
    cssRule(WMReducedMotion) { Modifier.styleModifier { property("animation", "none") } }
}

/**
 * The dialog's column: it fills the dialog's height, and the caller's content takes what the
 * title and the actions leave when its root uses [WMDialogFillStyle], so a fixed-height dialog (a
 * sheet) keeps its height. A dialog sized by its content is unaffected.
 */
val WMDialogBodyStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(WMDialogGapVar.value())
        .styleModifier {
            property("height", "100%")
            property("min-height", "0")
        }
}

/**
 * For the root of a dialog's content that should take the height the title and the actions leave
 * (the sheet's list); it must not grow past it (`min-height: 0`), so what it holds scrolls inside.
 */
val WMDialogFillStyle = CssStyle.base {
    Modifier.fillMaxWidth().styleModifier {
        property("flex", "1 1 0")
        property("min-height", "0")
    }
}

/** Actions stack full-width on phones with the main action first; from medium up they sit in a row. */
val WMDialogActionsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.ColumnReverse)
            .gap(WMInlineGapVar.value())
    }
    FSBreakpoint.Medium {
        Modifier.flexDirection(FlexDirection.Row).styleModifier { property("justify-content", "flex-end") }
    }
}

private val dialogElement = ElementBuilder.createBuilder<HTMLElement>("dialog")

/**
 * A modal dialog with a [title], an optional [description], caller-supplied [content] and an
 * [actions] row. [variant] widens it ([WMWideDialogVariant], [WMSheetDialogVariant]). Escape calls [onDismiss] unless [forceInteraction]. The dialog is shown with
 * `showModal()` when it enters the composition and closed when it leaves, so the caller
 * composes it conditionally.
 */
@Composable
fun WMDialog(
    title: String,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<WMDialogKind>? = null,
    description: String? = null,
    forceInteraction: Boolean = false,
    actions: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    TagElement(
        elementBuilder = dialogElement,
        applyAttrs = WMDialogStyle.toModifier(variant)
            .then(modifier)
            .toAttrs {
                attr("aria-labelledby", "wm-dialog-title")
                addEventListener("cancel") { event ->
                    event.preventDefault()
                    if (!forceInteraction) onDismiss()
                }
            }
    ) {
        DisposableEffect(Unit) {
            scopeElement.asDynamic().showModal()
            onDispose { scopeElement.asDynamic().close() }
        }
        Column(modifier = WMDialogBodyStyle.toModifier()) {
            Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()), verticalAlignment = Alignment.Top) {
                Column(modifier = Modifier.fillMaxWidth().gap(4.px)) {
                    H2(attrs = WMCardTitleTextStyle.toModifier().styleModifier { property("margin", "0") }.toAttrs { id("wm-dialog-title") }) {
                        Text(title)
                    }
                    description?.let { SpanText(it, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant)) }
                }
                if (!forceInteraction) {
                    WMIconButton(WMIconGlyph.X, accessibilityString = "Close", size = WMButtonSize.Sm, onClick = onDismiss)
                }
            }
            content()
            actions?.let { Row(modifier = WMDialogActionsStyle.toModifier()) { it() } }
        }
    }
}

/** Full-width container for dialog actions when composing them outside [WMDialog]. */
@Composable
fun WMDialogActions(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Row(modifier = WMDialogActionsStyle.toModifier().then(modifier)) { content() }
}

/** A dialog button stretches on phones. */
val WMDialogButtonStyle = CssStyle {
    base { Modifier.width(100.percent) }
    FSBreakpoint.Medium { Modifier.styleModifier { property("width", "auto") } }
}
